import {
  collection,
  doc,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  type DocumentData,
  type Unsubscribe,
} from "firebase/firestore";
import { getFirebaseApp } from "./firebaseClient";
import type { CourseChatMessage, CourseChatSettings } from "services/courseChatService";

export type FirestoreChatBootstrap = {
  firebase: {
    uid: string;
    course_chat_path: string;
    messages_path: string;
  };
  settings: {
    is_muted: boolean;
  };
  permissions: {
    can_read: boolean;
    can_send: boolean;
    can_mute: boolean;
  };
};

type FirestoreMessageDoc = {
  id: string;
  courseId: number;
  senderId: number;
  senderRole: string;
  senderName: string | null;
  senderAvatar: string | null;
  type: CourseChatMessage["message_type"];
  text: string | null;
  attachmentPath: string | null;
  attachmentName: string | null;
  attachmentMime: string | null;
  attachmentSize: number | null;
  voiceDurationSeconds: number | null;
  clientMessageId: string | null;
  createdAtMillis: number;
  isDeleted: boolean;
  deletedAtMillis?: number | null;
  deletedByRole?: string | null;
  deletedByName?: string | null;
};

function mapFirestoreMessage(docData: FirestoreMessageDoc, viewerAppUserId: number): CourseChatMessage {
  const isDeleted = Boolean(docData.isDeleted);
  // Keep attachmentPath in Firestore for audit, but never expose it to UI when deleted.
  const attachment: CourseChatMessage["attachment"] =
    !isDeleted && docData.attachmentPath
      ? {
          url: null as string | null,
          path: docData.attachmentPath,
          name: docData.attachmentName,
          mime: docData.attachmentMime,
          size: docData.attachmentSize,
          duration_seconds: docData.voiceDurationSeconds,
        }
      : null;

  return {
    id: docData.id,
    course_id: docData.courseId,
    sender: {
      id: docData.senderId,
      name: docData.senderName,
      role: docData.senderRole,
      avatar_url: docData.senderAvatar,
    },
    message_type: docData.type,
    body: isDeleted ? null : docData.text,
    attachment,
    client_message_id: docData.clientMessageId ?? null,
    is_mine: docData.senderId === viewerAppUserId,
    read_count: null,
    created_at: docData.createdAtMillis ? new Date(docData.createdAtMillis).toISOString() : null,
    created_at_millis: docData.createdAtMillis,
    created_at_human: null,
    is_deleted: isDeleted,
  };
}

export function listenToCourseChat(
  bootstrap: FirestoreChatBootstrap,
  viewerAppUserId: number,
  handlers: {
    onMessages: (messages: CourseChatMessage[]) => void;
    onSettings: (settings: CourseChatSettings) => void;
    onError?: (error: Error) => void;
  },
  messageLimit = 100
): Unsubscribe {
  const db = getFirestore(getFirebaseApp());
  const courseId = Number(bootstrap.firebase.course_chat_path.split("/").pop());
  const messagesRef = collection(db, bootstrap.firebase.messages_path);
  const messagesQuery = query(messagesRef, orderBy("createdAtMillis", "asc"), limit(messageLimit));

  const unsubMessages = onSnapshot(
    messagesQuery,
    (snapshot) => {
      const messages = snapshot.docs.map((item) =>
        mapFirestoreMessage(item.data() as FirestoreMessageDoc, viewerAppUserId)
      );
      handlers.onMessages(messages);
    },
    (error) => handlers.onError?.(error)
  );

  const chatRef = doc(db, bootstrap.firebase.course_chat_path);
  const unsubChat = onSnapshot(
    chatRef,
    (snapshot) => {
      const data = snapshot.data() as DocumentData | undefined;
      handlers.onSettings({
        course_id: courseId,
        is_muted: Boolean(data?.isMuted),
        muted_by: null,
        muted_at: null,
        mute_reason: null,
      });
    },
    (error) => handlers.onError?.(error)
  );

  return () => {
    unsubMessages();
    unsubChat();
  };
}
